import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { AppState } from '@redux/store'
import { ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { SubmitErrorResponse } from '@typesApp/SubmitResponse'
import { SubmitError, SubmitErrorType, SubmitType, SubmitResult } from '@typesApp/SubmitError'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { isGatewayTimeout } from '@utils/errorUtil'
import { isSubmittedStatus } from '@utils/claimUtil'
import { workflowSlice } from './workflowSlice'

export interface SubmitState {
  submitType: SubmitType
  submitError: SubmitError
  submitResult: SubmitResult
  lastSubmitError?: SubmitError
  submitting: boolean
}

const initialState = {
  submitType: SubmitType.DEFAULT,
  submitError: { type: SubmitErrorType.NONE },
  submitResult: SubmitResult.NONE,
  submitting: false,
} as SubmitState

export const claimSubmitSlice = createSlice({
  name: 'claimSubmit',
  initialState: initialState,
  reducers: {
    submitError(state, action: PayloadAction<SubmitError | SubmitErrorType>) {
      state.submitError = isSubmitError(action.payload) ? action.payload : { type: action.payload }
      state.submitResult = SubmitResult.ERROR
    },
    resetSubmitError(state) {
      state.submitError = { type: SubmitErrorType.NONE }
      state.submitResult = SubmitResult.NONE
    },
    claimAccepted(state) {
      state.submitError = { type: SubmitErrorType.NONE }
      state.submitResult = SubmitResult.ACCEPTED
    },
    setSubmitting(state, action: PayloadAction<boolean | { submitting: boolean; submitType: SubmitType }>) {
      state.submitting = typeof action.payload === 'boolean' ? action.payload : action.payload.submitting
      if (typeof action.payload !== 'boolean') {
        state.submitType = action.payload.submitType
      }
    },
    resetSubmitStatus(state) {
      return {
        ...initialState,

        // retain current submit state to allow feedback (modal) to be shown w/o the app drawer being visible
        submitError: state.submitError,
        submitResult: state.submitResult,
        lastSubmitError: state.lastSubmitError,
      }
    },
    resetClaimSubmitInitialState(state) {
      return initialState
    },
  },

  extraReducers: builder => {
    /**
     * Reset submit status on show drawer
     */
    builder.addCase(workflowSlice.actions.showApp, state => {
      state.submitError = { type: SubmitErrorType.NONE }
      state.submitResult = SubmitResult.NONE
    })

    /**
     * Update submitting status
     */
    builder.addMatcher(claimRecordsApi.endpoints.submitClaim.matchPending, state => {
      state.submitting = true
    })

    /**
     * Set submitting to false on error (ignore 504 or submitted status, they will be retried)
     */
    builder.addMatcher(claimRecordsApi.endpoints.submitClaim.matchRejected, (state, action) => {
      if (
        !isGatewayTimeout(action.payload?.status) &&
        !isSubmittedStatus((action.payload as SubmitErrorResponse)?.data?.claimRecord?.status)
      ) {
        state.submitting = false
      }
    })

    /**
     *  Reset last submit result on nullify
     */
    builder.addMatcher(claimRecordsApi.endpoints.nullifyClaim.matchFulfilled, state => {
      state.lastSubmitError = undefined
    })

    builder.addMatcher(claimRecordsApi.endpoints.getQuote.matchPending, state => {
      state.submitting = true
      state.submitType = SubmitType.REQUEST_QUOTE
      state.submitError = { type: SubmitErrorType.NONE }
    })

    builder.addMatcher(claimRecordsApi.endpoints.getQuote.matchRejected, (state, action) => {
      if (!isGatewayTimeout(action.payload?.status)) {
        state.submitError = { type: SubmitErrorType.REQUEST_QUOTE_ERROR }
        state.submitting = false
      }
    })

    builder.addMatcher(claimRecordsApi.endpoints.acceptQuote.matchPending, state => {
      state.submitting = true
      state.submitType = SubmitType.ACCEPT_QUOTE
      state.submitResult = SubmitResult.NONE
      state.submitError = { type: SubmitErrorType.NONE }
    })

    /**
     * Refuse quote
     */
    builder.addMatcher(claimRecordsApi.endpoints.refuseQuote.matchPending, state => {
      state.submitting = true
      state.submitType = SubmitType.REFUSE_QUOTE
      state.submitError = { type: SubmitErrorType.NONE }
    })

    builder.addMatcher(claimRecordsApi.endpoints.refuseQuote.matchFulfilled, (state, action) => {
      const emptyQuote = action.payload.claimRecord?.claimData?.quote === undefined
      const claimRecordStatus = action.payload.claimRecord?.status

      if (emptyQuote && claimRecordStatus === ClaimRecordStatus.QUOTE_REQUIRED) {
        state.submitting = false
      }
    })

    builder.addMatcher(claimRecordsApi.endpoints.refuseQuote.matchRejected, (state, action) => {
      if (!isGatewayTimeout(action.payload?.status)) {
        state.submitError = { type: SubmitErrorType.REFUSE_QUOTE_ERROR }
        state.submitting = false
      }
    })

    builder.addMatcher(claimRecordsApi.endpoints.syncInsuranceMember.matchFulfilled, state => {
      state.submitting = false
    })

    builder.addMatcher(claimRecordsApi.endpoints.syncInsuranceMember.matchRejected, (state, action) => {
      if (!isGatewayTimeout(action.payload?.status)) {
        state.submitting = false
      }
    })
  },
})

const isSubmitError = (payload: SubmitError | SubmitErrorType): payload is SubmitError => {
  return (payload as SubmitError).type ? true : false
}

export const {
  submitError,
  claimAccepted,
  setSubmitting,
  resetSubmitError,
  resetSubmitStatus,
  resetClaimSubmitInitialState,
} = claimSubmitSlice.actions

export const submitTypeSelector = (state: AppState): SubmitType => state.claimSubmit.submitType
export const submitResultSelector = (state: AppState): SubmitResult => state.claimSubmit.submitResult
export const submitErrorSelector = (state: AppState): SubmitError => state.claimSubmit.submitError
export const claimAcceptedSelector = (state: AppState): boolean =>
  state.claimSubmit.submitResult === SubmitResult.ACCEPTED
export const claimLastSubmitTimedoutSelector = (state: AppState): boolean =>
  state.claimSubmit?.lastSubmitError?.type === SubmitErrorType.TIMEOUT_RETRY
export const claimSubmittingSelector = (state: AppState): boolean => state.claimSubmit.submitting
